import { viewportHeight as layoutViewportHeight, viewportScrollY } from "../../utils/displayDensity";
/* eslint-disable */
import { Check, Info, Lock, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getHistorySeries, useXboxTitleHistories } from "../../services/xboxDataService";
import {
  XboxArt,
  XboxArtPlaceholder,
  XBOX_PLACEHOLDER_LOGO,
  ESTIMATED_PLAYERS_DISCLOSURE,
  fmtEstimated,
  changeTone,
  fmtChange,
  fmtInt,
  fmtShare,
  fmtSnapshot,
  typeLabel,
} from "./xboxUi";

const INITIAL = 60;
const BATCH = 60;

const COLUMNS = [
  { key: "rank", label: "Rank", sortable: true, defaultDir: "asc" },
  { key: "name", label: "Title", sortable: true, defaultDir: "asc" },
  { key: "releaseDate", label: "Release Date", sortable: true, defaultDir: "desc" },
  { key: "share", label: "Player Share", sortable: true, defaultDir: "desc" },
  { key: "estimated", label: "Current Users", title: ESTIMATED_PLAYERS_DISCLOSURE, sortable: true, defaultDir: "desc" },
  { key: "change", label: "Change", sortable: true, defaultDir: "desc" },
  { key: "devices", label: "Platform", sortable: false },
  { key: "more", label: "", sortable: false },
];

const sorters = {
  rank: (a, b) => Number(Boolean(a.isInactive)) - Number(Boolean(b.isInactive)) || a.rank - b.rank,
  name: (a, b) => a.title.name.localeCompare(b.title.name),
  releaseDate: (a, b) => new Date(a.title.releaseDate).getTime() - new Date(b.title.releaseDate).getTime(),
  share: (a, b) => a.share - b.share,
  estimated: (a, b) => a.estimatedPlayers - b.estimatedPlayers,
  users: (a, b) => a.activeUsers - b.activeUsers,
  // Titles with no prior snapshot sort after every measured change.
  change: (a, b) => (a.change ?? -Infinity) - (b.change ?? -Infinity),
};

function DeviceChips({ observed }) {
  const entries = Object.entries(observed || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <span className="xbox-muted">—</span>;
  return (
    <div className="xbox-device-chips">
      {entries.map(([label]) => (
        <span key={label} className="xbox-chip">{label.replace("Xbox ", "")}</span>
      ))}
    </div>
  );
}

function ShareSpark({ history }) {
  const pts = history.filter((p) => p.share != null);
  if (pts.length < 2) return null;
  const W = 220;
  const H = 46;
  const t0 = history[0].time;
  const t1 = history[history.length - 1].time;
  const max = Math.max(...pts.map((p) => p.share)) || 1;
  const x = (t) => (t1 === t0 ? W / 2 : ((t - t0) / (t1 - t0)) * (W - 8) + 4);
  const y = (v) => H - 6 - (v / max) * (H - 14);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${x(p.time).toFixed(1)},${y(p.share).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} className="xbox-spark" aria-hidden>
      <path d={d} fill="none" stroke="#3fa9f5" strokeWidth="2" strokeLinejoin="round" />
      {pts.map((p) => (
        <circle key={p.time} cx={x(p.time)} cy={y(p.share)} r="3" fill="#3fa9f5" />
      ))}
    </svg>
  );
}

function RowDetail({ row, dataset, mode, inChart, onToggleChart }) {
  const history = useXboxTitleHistories(dataset, [row.titleId]);
  const shareHistory = dataset?.compact
    ? getHistorySeries(dataset, mode, [row.titleId]).map(point => ({ time: point.time, share: point[row.titleId] }))
    : row.shareHistory;
  const t = { ...row.title, ...dataset?.histories?.get(row.titleId)?.details };
  const observed = Object.entries(row.observedDevices || {}).sort((a, b) => b[1] - a[1]);
  const observedTotal = observed.reduce((s, [, v]) => s + v, 0);
  const release = t.releaseDate ? new Date(t.releaseDate) : null;

  return (
    <div className="xbox-row-detail" onClick={(e) => e.stopPropagation()}>
      <div className="xbox-detail-col xbox-detail-desc">
        <div className="xbox-detail-label">About</div>
        <p>{history.loading ? "Loading title details…" : t.shortDescription || t.description || "No description available from Microsoft."}</p>
      </div>

      <div className="xbox-detail-col">
        <div className="xbox-detail-label">Details</div>
        <dl className="xbox-detail-list">
          <dt>Developer</dt><dd>{t.developer || "—"}</dd>
          <dt>Publisher</dt><dd>{t.publisher || "—"}</dd>
          <dt>Released</dt><dd>{release && !Number.isNaN(release.getTime()) ? release.toLocaleDateString() : "—"}</dd>
          <dt>Genres</dt><dd>{t.genres.length ? t.genres.join(", ") : "—"}</dd>
          <dt>Supports</dt><dd>{t.supportedDevices.length ? t.supportedDevices.join(", ") : "—"}</dd>
          <dt>Game Pass</dt><dd>{t.gamePass == null ? "—" : t.gamePass ? "Yes" : "No"}</dd>
          <dt>Title ID</dt><dd className="xbox-mono">{t.titleId}</dd>
        </dl>
      </div>

      <div className="xbox-detail-col">
        <div className="xbox-detail-label">Observed by console</div>
        {observed.length ? (
          observed.map(([label, n]) => (
            <div key={label} className="xbox-device-bar">
              <span>{label}</span>
              <div className="xbox-bar-track">
                <div className="xbox-bar-fill" style={{ width: `${observedTotal ? (n / observedTotal) * 100 : 0}%` }} />
              </div>
              <b>{fmtInt(n)}</b>
            </div>
          ))
        ) : (
          <span className="xbox-muted">No console breakdown.</span>
        )}
      </div>

      <div className="xbox-detail-col">
        <div className="xbox-detail-label">Player Share by snapshot</div>
        {history.loading && <div role="status">Loading title history…</div>}
        {history.error && <div role="alert">{history.error} <button type="button" onClick={history.retry}>Retry history</button></div>}
        <ShareSpark history={shareHistory} />
        <div className="xbox-detail-history">
          {shareHistory.map((p) => (
            <div key={p.time}>
              <span>{fmtSnapshot(p.time)}</span>
              <b>{p.share == null ? "not observed" : fmtShare(p.share)}</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function XboxRow({ row, dataset, mode, expanded, onToggle, inChart, onToggleChart, maxShare, rowRef, readOnly = false }) {
  const t = row.title;
  const tone = changeTone(row.change);
  const genre = t.genres[0];
  const metaBits = [t.publisher, genre].filter(Boolean);

  return (
    <div
      ref={rowRef}
      className={`xbox-row${expanded ? " is-expanded" : ""}`}
      onClick={() => onToggle(row.titleId)}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(row.titleId);
        }
      }}
    >
      <div className="xbox-row-bg" aria-hidden>
        <XboxArt candidates={t.backgroundArt} width={1400} className="xbox-row-bg-img" />
      </div>
      <div className="xbox-row-shade" aria-hidden />

      <div className="xbox-row-grid">
        <div className="xbox-cell-rank">#{row.rank}</div>

        <div className="xbox-cell-title">
          <div className="xbox-row-art">
            <XboxArt candidates={t.mainArt} width={160} alt="" fallback={<XboxArtPlaceholder />} />
          </div>
          <div className="xbox-title-text">
            <div className="xbox-title-name">{t.name}</div>
            <div className="xbox-title-meta">
              {metaBits.map((b, i) => (
                <span key={i}>{b}</span>
              ))}
            </div>
            <div className="xbox-title-badges">
              <span className={`xbox-type-badge is-${t.type.toLowerCase()}`}>{typeLabel(t.type)}</span>
              {t.gamePass && <span className="xbox-gp-badge">Game Pass</span>}
            </div>
          </div>
        </div>

        <div className="xbox-cell-release">
          {t.releaseDate && Number.isFinite(new Date(t.releaseDate).getTime()) ? new Date(t.releaseDate).toLocaleDateString("en-US", { month: "numeric", day: "2-digit", year: "numeric" }) : "—"}
        </div>

        <div className="xbox-cell-share">
          <b>{fmtShare(row.share)}</b>
          <div className="xbox-bar-track">
            <div className="xbox-bar-fill" style={{ width: `${maxShare ? Math.max(1.5, (row.share / maxShare) * 100) : 0}%` }} />
          </div>
        </div>

        <div className="xbox-cell-estimated" title={`${ESTIMATED_PLAYERS_DISCLOSURE} Exact estimate: ${fmtEstimated(row.estimatedPlayers, true)}.`}>
          <b>{fmtEstimated(row.estimatedPlayers)}</b>
        </div>

        <div className="xbox-cell-num">
          {row.isNew ? (
            <span className="xbox-change is-new">New</span>
          ) : (
            <span className={`xbox-change is-${tone}`}>{fmtChange(row.change)}</span>
          )}
          {row.prevRank != null && row.prevRank !== row.rank && (
            <span className="xbox-rank-move">{row.prevRank > row.rank ? "▲" : "▼"} {Math.abs(row.prevRank - row.rank)}</span>
          )}
        </div>

        <div className="xbox-cell-devices">
          <DeviceChips observed={row.observedDevices} />
        </div>

        <div className="xbox-cell-more">
          {!readOnly && <button
            type="button"
            className={`xbox-row-chart-btn${inChart ? " is-on" : ""}`}
            title={inChart ? "Remove from history chart" : "Add to history chart"}
            aria-label={inChart ? `Remove ${t.name} from history chart` : `Add ${t.name} to history chart`}
            onClick={(event) => {
              event.stopPropagation();
              onToggleChart(row.titleId);
            }}
          >
            {inChart ? <Check size={15} /> : <Plus size={15} />}
          </button>}
        </div>
      </div>

      {expanded && <RowDetail row={row} dataset={dataset} mode={mode} inChart={inChart} onToggleChart={onToggleChart} />}
    </div>
  );
}

export default function XboxActivityTable({ rows, dataset, mode, loading, chartIds, onToggleChart, focusTitleId, onFocusHandled, readOnly = false }) {
  const [sort, setSort] = useState({ key: "rank", dir: "asc" });
  const [expandedId, setExpandedId] = useState(null);
  const [showCount, setShowCount] = useState(INITIAL);
  const rowRefs = useRef(new Map());

  const sorted = useMemo(() => {
    const fn = sorters[sort.key] || sorters.rank;
    if (sort.key === "releaseDate") {
      return rows.slice().sort((first, second) => {
        const firstDate = first.title.releaseDate ? new Date(first.title.releaseDate).getTime() : NaN;
        const secondDate = second.title.releaseDate ? new Date(second.title.releaseDate).getTime() : NaN;
        if (!Number.isFinite(firstDate)) return Number.isFinite(secondDate) ? 1 : 0;
        if (!Number.isFinite(secondDate)) return -1;
        return sort.dir === "desc" ? secondDate - firstDate : firstDate - secondDate;
      });
    }
    const out = rows.slice().sort(fn);
    if (sort.dir === "desc") out.reverse();
    if (!["rank", "share", "estimated", "users"].includes(sort.key)) return out;
    const inactiveOrder = new Map(rows.filter((row) => row.isInactive).map((row, index) => [row.titleId, index]));
    const valueKey = sort.key === "estimated" ? "estimatedPlayers" : sort.key === "users" ? "activeUsers" : sort.key;
    return out.sort((first, second) => first.isInactive && second.isInactive && first[valueKey] === second[valueKey]
      ? inactiveOrder.get(first.titleId) - inactiveOrder.get(second.titleId)
      : 0);
  }, [rows, sort]);

  const maxShare = useMemo(() => rows.reduce((m, r) => Math.max(m, r.share), 0), [rows]);

  useEffect(() => setShowCount(INITIAL), [rows, sort]);

  useEffect(() => {
    const onScroll = () => {
      if (layoutViewportHeight() + viewportScrollY() + 900 >= document.documentElement.offsetHeight) {
        setShowCount((n) => Math.min(n + BATCH, sorted.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sorted.length]);

  // A card click asks the table to reveal and expand that title.
  useEffect(() => {
    if (!focusTitleId) return;
    const idx = sorted.findIndex((r) => r.titleId === focusTitleId);
    if (idx < 0) {
      onFocusHandled?.();
      return;
    }
    setExpandedId(focusTitleId);
    setShowCount((n) => Math.max(n, idx + 10));
    requestAnimationFrame(() => {
      rowRefs.current.get(focusTitleId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      onFocusHandled?.();
    });
  }, [focusTitleId, sorted]);

  const onHeader = (col) => {
    if (!col.sortable || readOnly) return;
    setSort((s) => (s.key === col.key ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: col.defaultDir }));
  };

  return (
    <div className="xbox-table">
      <div className="xbox-table-head xbox-row-grid">
        {COLUMNS.map((col) => (
          <button
            key={col.key}
            type="button"
            className={`xbox-th${col.sortable ? " is-sortable" : ""}${sort.key === col.key ? " is-sorted" : ""}`}
            title={col.title}
            aria-sort={sort.key === col.key ? sort.dir === "asc" ? "ascending" : "descending" : undefined}
            onClick={() => onHeader(col)}
            disabled={!col.sortable}
          >
            {col.label}
            {col.key === "estimated" && <Info size={11} className="xbox-th-info" aria-hidden="true" />}
            {sort.key === col.key && <span className="xbox-sort-caret">{sort.dir === "asc" ? "▲" : "▼"}</span>}
          </button>
        ))}
      </div>

      {readOnly ? (
        // Locked public preview: ghost rows under a veil from the first row
        // down, with sorting, expansion, and chart toggles disabled.
        <div className="xbox-table-veil-wrap">
          <div inert="">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <div key={i} className="xbox-row-skel" />)
            ) : (
              sorted.slice(0, 12).map((row) => (
                <XboxRow
                  dataset={dataset}
                  mode={mode}
                  key={row.titleId}
                  row={row}
                  maxShare={maxShare}
                  expanded={false}
                  onToggle={() => {}}
                  inChart={false}
                  onToggleChart={() => {}}
                  readOnly
                />
              ))
            )}
          </div>
          <div className="mg-mindshare-preview-veil xbox-table-veil" aria-hidden="true" />
          <div className="mg-mindshare-preview-cta xbox-table-cta" role="note" aria-label="Xbox full access">
            <Lock size={22} aria-hidden="true" />
            <h3>Full Xbox access for paying members</h3>
            <p>
              The full activity table, filters, and history charts are available to members.
              Contact <a href="mailto:info@mindgamedata.com">info@mindgamedata.com</a> for details.
            </p>
          </div>
        </div>
      ) : loading ? (
        Array.from({ length: 8 }).map((_, i) => <div key={i} className="xbox-row-skel" />)
      ) : sorted.length === 0 ? (
        <div className="xbox-empty">
          <img src={XBOX_PLACEHOLDER_LOGO} alt="" />
          <div>No titles match the current filters.</div>
        </div>
      ) : (
        sorted.slice(0, showCount).map((row) => (
          <XboxRow
            dataset={dataset}
            mode={mode}
            key={row.titleId}
            row={row}
            maxShare={maxShare}
            expanded={expandedId === row.titleId}
            onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
            inChart={chartIds.includes(row.titleId)}
            onToggleChart={onToggleChart}
            rowRef={(el) => {
              if (el) rowRefs.current.set(row.titleId, el);
              else rowRefs.current.delete(row.titleId);
            }}
          />
        ))
      )}

      {!loading && !readOnly && showCount < sorted.length && (
        <button type="button" className="xbox-show-more" onClick={() => setShowCount((n) => n + BATCH)}>
          Show more ({fmtInt(sorted.length - showCount)} more)
        </button>
      )}
    </div>
  );
}
